import { motion, useReducedMotion, type Variants } from "motion/react";
import { useTranslation } from "react-i18next";
import { Check, MapPin, Users } from "lucide-react";
import { fadeUpSm } from "./motion-presets";

/**
 * Stylized night-map dispatch mock for the hero. Mirrors the real
 * LocationTracking UI vocabulary (initial avatars, status dots, job chips)
 * and tells the same scene as the phone screenshot overlaid on the right:
 * Marcus Bennett en route in Brickell, ETA 4 min.
 */

// ── Map geometry (viewBox 0 0 800 400) ──────────────────────────────────
// Streets drawn twice: a wide background-colored "casing" pass that carves
// through the blocks, then a thin centerline. Slight drift per path keeps
// the grid from reading as synthetic.
const STREETS = [
  { d: "M 92 0 L 84 400", w: 1.5, cas: 10 },
  { d: "M 186 0 L 174 400", w: 2.5, cas: 14 },
  { d: "M 302 0 L 296 400", w: 1.5, cas: 10 },
  { d: "M 414 0 L 426 400", w: 2.5, cas: 14 },
  { d: "M 556 0 L 566 400", w: 1.5, cas: 10 },
  { d: "M 684 0 L 678 400", w: 1.5, cas: 10 },
  { d: "M 0 66 L 800 58", w: 1.5, cas: 10 },
  { d: "M 0 140 L 800 124", w: 2.5, cas: 14 },
  { d: "M 0 206 L 800 198", w: 1.5, cas: 10 },
  { d: "M 0 262 L 800 276", w: 2.5, cas: 14 },
  { d: "M 0 334 L 800 342", w: 1.5, cas: 10 },
  { d: "M -20 380 L 300 220 L 560 120 L 820 40", w: 3.5, cas: 18 },
] as const;

const BLOCKS: Array<{ x: number; y: number; w: number; h: number; r?: string; park?: boolean }> = [
  { x: 100, y: 18, w: 72, h: 36 },
  { x: 196, y: 20, w: 92, h: 32 },
  { x: 312, y: 16, w: 232, h: 38, r: "rotate(-0.8 428 35)" },
  { x: 572, y: 22, w: 96, h: 30 },
  { x: 692, y: 18, w: 96, h: 36 },
  { x: 100, y: 74, w: 188, h: 50 },
  { x: 312, y: 72, w: 96, h: 52 },
  { x: 436, y: 70, w: 112, h: 50, r: "rotate(0.7 492 95)" },
  { x: 572, y: 66, w: 220, h: 54 },
  { x: 196, y: 144, w: 92, h: 50 },
  { x: 312, y: 140, w: 236, h: 56 },
  { x: 572, y: 134, w: 96, h: 58 },
  { x: 692, y: 132, w: 100, h: 60 },
  { x: 312, y: 216, w: 96, h: 42 },
  { x: 436, y: 214, w: 112, h: 44, park: true },
  { x: 572, y: 214, w: 96, h: 44 },
  { x: 100, y: 282, w: 188, h: 46, r: "rotate(-0.6 194 305)" },
  { x: 436, y: 280, w: 112, h: 48 },
  { x: 572, y: 282, w: 96, h: 46 },
  { x: 692, y: 280, w: 100, h: 50 },
];

// Map decoration — street names don't localize, same as real map tiles.
const STREET_LABELS = [
  { t: "SW 8TH ST", x: 64, y: 122, r: "rotate(-1.1 64 122)" },
  { t: "CORAL WAY", x: 606, y: 258, r: "rotate(1 606 258)" },
  { t: "BRICKELL AVE", x: 434, y: 232, r: "rotate(88.3 434 232)" },
  { t: "SW 2ND AVE", x: 100, y: 190, r: "rotate(90 100 190)" },
  { t: "S MIAMI AVE", x: 352, y: 204, r: "rotate(-21 352 204)" },
] as const;

// Follows the streets: up the diagonal, then north on Oak St to the job.
// Destination stays in the upper-left map zone so the phone overlay
// (bottom-right) never covers point B — on any breakpoint.
const ROUTE_D = "M 148 296 L 300 220 L 301 96";

type TechStatus = "enRoute" | "onSite" | "available";

const STATUS_RING: Record<TechStatus, string> = {
  enRoute: "border-accent",
  onSite: "border-success",
  available: "border-border",
};

const MAP_TECHS: Array<{
  initials: string;
  status: TechStatus;
  left: string;
  top: string;
  className?: string;
}> = [
  { initials: "MB", status: "enRoute", left: "18.5%", top: "74%" },
  { initials: "DR", status: "onSite", left: "62%", top: "58%", className: "max-sm:hidden" },
  { initials: "AJ", status: "available", left: "12.5%", top: "38%", className: "max-sm:hidden" },
];

const SIDEBAR_TECHS = [
  { initials: "MB", name: "Marcus Bennett", avatar: "bg-accent text-accent-foreground", dot: "bg-success", metaKey: "onlineNow", chipKey: "statusEnRoute", chip: "bg-accent/10 text-accent", chipDot: "bg-accent", pulse: true, selected: true },
  { initials: "DR", name: "Dana Ruiz", avatar: "bg-primary text-primary-foreground", dot: "bg-success", metaKey: "online1m", chipKey: "statusOnSite", chip: "bg-success/10 text-success", chipDot: "bg-success", pulse: false, selected: false },
  { initials: "AJ", name: "Andre Jackson", avatar: "bg-success text-success-foreground", dot: "bg-warning", metaKey: "away12m", chipKey: "statusScheduled", chip: "bg-muted text-muted-foreground", chipDot: "bg-muted-foreground", pulse: false, selected: false },
] as const;

const overlayStagger: Variants = {
  hidden: {},
  visible: { transition: { delayChildren: 0.9, staggerChildren: 0.15 } },
};

const pinPop: Variants = {
  hidden: { opacity: 0, scale: 0.4 },
  visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 260, damping: 18 } },
};

const TechPin = ({
  initials,
  status,
  left,
  top,
  className = "",
}: (typeof MAP_TECHS)[number]) => (
  <motion.div
    variants={pinPop}
    style={{ left, top }}
    className={`absolute -translate-x-1/2 -translate-y-1/2 ${className}`}
  >
    <span className="relative flex h-9 w-9 items-center justify-center">
      {status === "enRoute" && (
        <span className="absolute inset-0 rounded-full bg-accent/40 motion-safe:animate-ping [animation-duration:2.2s]" />
      )}
      <span
        className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 ${STATUS_RING[status]} bg-card text-[10px] font-semibold text-foreground shadow-lg shadow-black/25`}
      >
        {initials}
      </span>
      {status === "onSite" && (
        <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-success ring-2 ring-background">
          <Check className="h-2 w-2 text-success-foreground" strokeWidth={3.5} />
        </span>
      )}
      {status === "enRoute" && (
        <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-accent ring-2 ring-background" />
      )}
    </span>
  </motion.div>
);

const RouteLayer = ({ reduce }: { reduce: boolean }) => (
  <g strokeLinecap="round" strokeLinejoin="round" fill="none">
    <motion.path
      d={ROUTE_D}
      stroke="hsl(var(--primary) / 0.55)"
      strokeWidth="7"
      filter="url(#wl-glow)"
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 0.6 }}
      transition={{ delay: 1.9, duration: 0.5 }}
    />
    <motion.path
      d={ROUTE_D}
      stroke="url(#wl-route)"
      strokeWidth="3"
      initial={reduce ? false : { pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ delay: 0.5, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
    />
    {!reduce && (
      <path
        d={ROUTE_D}
        pathLength={100}
        stroke="hsl(var(--accent-foreground) / 0.9)"
        strokeWidth="1.5"
        strokeDasharray="1.5 6"
        className="wl-route-flow"
        style={{ opacity: 0.85 }}
      />
    )}
  </g>
);

export const DispatchMapPanel = () => {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion() ?? false;

  return (
    <div className="flex h-[320px] md:h-[400px]">
      {/* Technicians sidebar — mirrors the real LocationTracking panel */}
      <div className="hidden sm:flex w-44 md:w-56 shrink-0 flex-col border-r border-border bg-card/60">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Users className="h-3.5 w-3.5 text-primary" />
          <span className="text-[11px] font-medium text-foreground">{t("hero.mockup.technicians")}</span>
          <span className="ml-auto rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground tabular-nums">3</span>
        </div>
        <div className="flex flex-col gap-1.5 p-2">
          {SIDEBAR_TECHS.map((tech) => (
            <div
              key={tech.initials}
              className={`rounded-lg border p-2 ${
                tech.selected ? "border-primary/60 bg-primary/10" : "border-transparent bg-card/40 opacity-85"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold ${tech.avatar}`}>
                    {tech.initials}
                  </span>
                  <span className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-card ${tech.dot}`} />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[11px] font-medium leading-tight text-foreground">{tech.name}</div>
                  <div className="truncate text-[9px] text-muted-foreground">{t(`hero.mockup.${tech.metaKey}`)}</div>
                </div>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${tech.chip}`}>
                  <span className={`h-1 w-1 rounded-full ${tech.chipDot} ${tech.pulse ? "motion-safe:animate-pulse" : ""}`} />
                  {t(`hero.mockup.${tech.chipKey}`)}
                </span>
                {tech.selected && (
                  <span className="mono-kpi text-[9px] font-semibold text-foreground">{t("hero.mockup.eta")}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Night map */}
      <div className="relative flex-1 overflow-hidden bg-background">
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 50% 40% at 35% 35%, hsl(var(--primary) / 0.15), transparent 60%), radial-gradient(ellipse 50% 40% at 70% 65%, hsl(var(--accent) / 0.12), transparent 60%)",
          }}
        />

        <svg viewBox="0 0 800 400" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden>
          <defs>
            <linearGradient id="wl-route" gradientUnits="userSpaceOnUse" x1="148" y1="296" x2="301" y2="96">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="hsl(var(--accent))" />
            </linearGradient>
            <filter id="wl-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="4" />
            </filter>
          </defs>

          {/* Miami River along the left edge */}
          <path
            d="M 0 0 L 48 0 C 32 70, 60 150, 42 230 C 30 300, 54 356, 40 400 L 0 400 Z"
            fill="hsl(var(--accent) / 0.07)"
          />
          <path
            d="M 48 0 C 32 70, 60 150, 42 230 C 30 300, 54 356, 40 400"
            fill="none"
            stroke="hsl(var(--accent) / 0.25)"
            strokeWidth="1.5"
          />

          {BLOCKS.map((b, i) => (
            <rect
              key={i}
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              rx={6}
              transform={b.r}
              fill={b.park ? "hsl(var(--success) / 0.10)" : "hsl(var(--border) / 0.30)"}
              stroke={b.park ? "hsl(var(--success) / 0.20)" : "hsl(var(--border) / 0.35)"}
              strokeWidth="1"
            />
          ))}

          {STREETS.map((s, i) => (
            <path key={`c${i}`} d={s.d} fill="none" stroke="hsl(var(--background))" strokeWidth={s.cas} strokeLinejoin="round" />
          ))}
          {STREETS.map((s, i) => (
            <path key={`l${i}`} d={s.d} fill="none" stroke="hsl(var(--border) / 0.65)" strokeWidth={s.w} strokeLinejoin="round" />
          ))}

          <g className="max-sm:hidden" fill="hsl(var(--muted-foreground))" opacity="0.38" fontSize="9" fontWeight="500" letterSpacing="2">
            {STREET_LABELS.map((l) => (
              <text key={l.t} x={l.x} y={l.y} transform={l.r}>
                {l.t}
              </text>
            ))}
          </g>

          <RouteLayer reduce={reduce} />
        </svg>

        {/* Pins + floating cards */}
        <motion.div
          initial={reduce ? "visible" : "hidden"}
          animate="visible"
          variants={overlayStagger}
          className="absolute inset-0"
        >
          {MAP_TECHS.map((tech) => (
            <TechPin key={tech.initials} {...tech} />
          ))}

          {/* Job destination — Herrera residence (point B, always visible) */}
          <motion.div variants={pinPop} className="absolute left-[37.6%] top-[22.5%] -translate-x-1/2 -translate-y-1/2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-4 ring-primary/15">
              <MapPin className="h-4 w-4" />
            </span>
            <div className="mt-1 whitespace-nowrap text-center text-[8px] font-medium text-foreground/70">
              Herrera Residence
            </div>
          </motion.div>

          {/* Active job card — same scene the customer sees on the phone */}
          <motion.div
            variants={fadeUpSm}
            className="absolute left-[38%] top-[34%] hidden lg:block w-56 rounded-xl border border-border bg-card/70 p-3 backdrop-blur-md shadow-[0_1px_0_0_hsl(var(--border)/0.6)_inset,0_8px_24px_-8px_rgba(0,0,0,0.45),0_2px_6px_-2px_rgba(0,0,0,0.3)]"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold text-foreground">{t("hero.mockup.jobTitle")}</div>
                <div className="mt-0.5 truncate text-[10px] text-muted-foreground">1120 Brickell Bay Dr · Sofia Herrera</div>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] font-medium text-accent">
                <span className="h-1 w-1 rounded-full bg-accent motion-safe:animate-pulse" />
                {t("hero.mockup.statusEnRoute")}
              </span>
            </div>
            <div className="mt-2.5 flex items-center justify-between border-t border-border/60 pt-2">
              <div className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-accent/50 bg-card text-[8px] font-semibold text-foreground">
                  MB
                </span>
                <span className="text-[10px] text-muted-foreground">Marcus B.</span>
              </div>
              <span className="mono-kpi text-[10px] font-semibold text-foreground">{t("hero.mockup.eta")}</span>
            </div>
          </motion.div>

          {/* Day summary pill */}
          <motion.div
            variants={fadeUpSm}
            className="absolute bottom-3 left-3 hidden md:inline-flex items-center gap-2.5 rounded-full border border-border bg-background/80 px-3 py-1.5 text-[10px] backdrop-blur"
          >
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-success" /> 2
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-warning" /> 1
            </span>
            <span className="text-muted-foreground/60">·</span>
            <span className="font-medium text-foreground/80">{t("hero.mockup.summary")}</span>
          </motion.div>
        </motion.div>

        {/* Live badge */}
        <div className="absolute left-3 top-3 inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-2.5 py-1 text-xs backdrop-blur">
          <span className="relative inline-flex">
            <span className="absolute inset-0 rounded-full bg-success opacity-60 motion-safe:animate-ping" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-success" />
          </span>
          <span className="font-medium text-foreground/80">{t("hero.mockup.live")}</span>
        </div>

        {/* Grounding fade under the phone overlay */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background/60 to-transparent" />
      </div>
    </div>
  );
};
